/**
 * @file Composable for hover-based post-action keyboard shortcuts on the surface
 * (e.g. the "D" shortcut that duplicates whichever post is currently hovered).
 */
import { trackEvent } from '@@/bits/analytics'
import { isActiveElementAnInputField } from '@@/bits/dom'
import { isPostPinned } from '@@/bits/post_state'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import type { Post } from '@@/types'
import { usePostActionMenuOptions } from '@@/vuecomposables/usePostActionMenuOptions'
import { isHoverPostShortcutsEnabled } from '@@/vuecomposables/useSurfacePostHoverHandlers'
import { PostActionType } from '@@/vuexstore/helpers/post'
import { storeToRefs } from 'pinia'
import type { KeyBindingMap } from 'tinykeys'

const useOnSurfacePostHover = (): {
  addHoverOnPostShortcuts: (shortcuts: KeyBindingMap) => void
} => {
  // When the feature is off (touchable / flag-off / a11y-off / demo users can never fire a shortcut),
  // bail before building the surface-wide, always-alive usePostActionMenuOptions instance below.
  if (!isHoverPostShortcutsEnabled) {
    return { addHoverOnPostShortcuts: () => {} }
  }

  const surfacePostActionStore = useSurfacePostActionStore()
  const { hoveredPost } = storeToRefs(surfacePostActionStore)

  const { actionHandlers, canPerformAction } = usePostActionMenuOptions({
    post: hoveredPost,
    platform: 'web',
    isExpanded: false,
  })
  const { isOverlayVisible } = storeToRefs(useSurfaceStore())

  const surfacePostsStore = useSurfacePostsStore()
  const { postBeingEditedCid } = storeToRefs(surfacePostsStore)

  function createHoveredPostShortcutHandler({
    actionType,
    analyticsKey,
    analyticsAction,
  }: {
    /**
     * Most shortcuts map to a fixed action (PostActionType). However, the "P" toggle resolves
     * Pin vs Unpin (and its analytics label) from the hovered post's current pin state, so we
     * accept a resolver too.
     */
    actionType: PostActionType | ((post: Post) => PostActionType)
    analyticsKey: string
    analyticsAction: string | ((post: Post) => string)
  }): (event: KeyboardEvent) => void {
    return (event: KeyboardEvent): void => {
      if (event.repeat) return
      if (postBeingEditedCid.value) return
      if (isOverlayVisible.value) return
      if (isActiveElementAnInputField()) return
      const post = hoveredPost.value
      if (post == null) return
      const resolvedActionType = typeof actionType === 'function' ? actionType(post) : actionType
      if (!canPerformAction(resolvedActionType)) return
      event.preventDefault()
      trackEvent('Post action menu keyboard shortcuts', 'Triggered shortcut', null, null, {
        target: `post-action-menu-shortcut-key-${analyticsKey}`,
        action: typeof analyticsAction === 'function' ? analyticsAction(post) : analyticsAction,
      })
      // canPerformAction was already checked above, so invoke the handler directly.
      void actionHandlers.value[resolvedActionType]?.()
    }
  }

  const handleOnKeydownS = createHoveredPostShortcutHandler({
    actionType: PostActionType.StartSlideshowFromThisPost,
    analyticsKey: 's',
    analyticsAction: 'start slideshow from this post',
  })
  const handleOnKeydownO = createHoveredPostShortcutHandler({
    actionType: PostActionType.ExpandPost,
    analyticsKey: 'o',
    analyticsAction: 'open post',
  })
  const handleOnKeydownL = createHoveredPostShortcutHandler({
    actionType: PostActionType.CopyPostLink,
    analyticsKey: 'l',
    analyticsAction: 'copy link to post',
  })
  const handleOnKeydownE = createHoveredPostShortcutHandler({
    actionType: PostActionType.EditPost,
    analyticsKey: 'e',
    analyticsAction: 'edit post',
  })
  const handleOnKeydownD = createHoveredPostShortcutHandler({
    actionType: PostActionType.DuplicatePostToCurrentWall,
    analyticsKey: 'd',
    analyticsAction: 'duplicate post',
  })
  const handleOnKeydownA = createHoveredPostShortcutHandler({
    actionType: PostActionType.DownloadAttachment,
    analyticsKey: 'a',
    analyticsAction: 'download attachment',
  })
  const handleOnKeydownBackspace = createHoveredPostShortcutHandler({
    actionType: PostActionType.DeletePost,
    analyticsKey: 'backspace',
    analyticsAction: 'delete post',
  })
  const handleOnKeydownF = createHoveredPostShortcutHandler({
    actionType: PostActionType.BringToFront,
    analyticsKey: 'f',
    analyticsAction: 'bring to front',
  })
  const handleOnKeydownB = createHoveredPostShortcutHandler({
    actionType: PostActionType.SendToBack,
    analyticsKey: 'b',
    analyticsAction: 'send to back',
  })
  const handleOnKeydownV = createHoveredPostShortcutHandler({
    actionType: PostActionType.StartFieldTripFromThisPost,
    analyticsKey: 'v',
    analyticsAction: 'start virtual tour from this post',
  })

  /**
   * The "P" shortcut toggles between Pin and Unpin based on the hovered post's current pin state —
   * mirroring how the action menu itself picks between "Pin post" and "Unpin post".
   */
  const handleOnKeydownP = createHoveredPostShortcutHandler({
    actionType: (post) => (isPostPinned(post) ? PostActionType.UnpinPost : PostActionType.PinPost),
    analyticsKey: 'p',
    analyticsAction: (post) => (isPostPinned(post) ? 'unpin post' : 'pin post'),
  })

  function addHoverOnPostShortcuts(shortcuts: KeyBindingMap): void {
    shortcuts.KeyS = handleOnKeydownS
    shortcuts.KeyO = handleOnKeydownO
    shortcuts.KeyL = handleOnKeydownL
    shortcuts.KeyE = handleOnKeydownE
    shortcuts.KeyP = handleOnKeydownP
    shortcuts.KeyD = handleOnKeydownD
    shortcuts.KeyA = handleOnKeydownA
    shortcuts.Backspace = handleOnKeydownBackspace
    shortcuts.KeyF = handleOnKeydownF
    shortcuts.KeyB = handleOnKeydownB
    shortcuts.KeyV = handleOnKeydownV
  }

  return { addHoverOnPostShortcuts }
}

export default useOnSurfacePostHover
